import { useEffect, useMemo } from "react";
import { motion } from "motion/react";

import { type Conversation } from "shared/apiClient";
import { AccountType } from "shared/types";
import { cn, getText, interpolate, type Noticeboard } from "shared/lib";
import { animationVariants } from "./lib";

import {
  useGetReportLoadingIndicatorConfig,
  useLatestMessage,
  useSkipAnalysisLayoutAnimations,
} from "@/hooks";
import {
  useIsReportGenerating,
  useIsReportReGenerating,
} from "@/components/analysisLayout/hooks";

import { PermitRequirementsReport, Separator } from "shared/components";
import { AnalysisTimeline } from "@/components/analysisTimeline";
import { ReportWarningPanel } from "@/components/wizard/ReportWarningPanel";
import { PermitRequirementsLoaderContainer } from "@/components/analysisLayout/permitRequirementsLoader";
import { MultiLingualDisclaimer } from "@/components/analysisLayout/multiLingualDisclaimer";

export interface PermitRequirementsProps {
  conversation: Conversation | undefined | null;
  messages: Conversation["messages"];
  noticeboardData: Noticeboard | undefined | null;
  printable?: boolean;
}

export function PermitRequirements({
  conversation,
  messages,
  noticeboardData,
  printable,
}: PermitRequirementsProps) {
  const [skipAnimations, setSkipAnimations] = useSkipAnalysisLayoutAnimations();

  useEffect(() => {
    setTimeout(() => {
      setSkipAnimations(true);
    }, 2000);
  }, [setSkipAnimations]);

  const latestUserMessage = useLatestMessage({
    senderAccountType: AccountType.USER,
  });

  const reportGenerating = useIsReportGenerating(conversation);
  const reportReGenerating = useIsReportReGenerating(conversation);

  // Since messages can come through via channel, merge messages into conversation
  const mergedConversation = useMemo(() => {
    return {
      ...conversation,
      messages: [...(messages || [])],
    };
  }, [conversation, messages]);

  const reportLoadingIndicatorConfig = useGetReportLoadingIndicatorConfig(
    conversation,
    noticeboardData,
  );

  return (
    <div className={cn("flex flex-col gap-10", printable && "gap-5")}>
      <div className="overflow-hidden">
        <motion.div
          className="flex flex-col gap-1 [transform-origin:top_left]"
          variants={animationVariants}
          initial={!skipAnimations && "hidden"}
          animate={["heading", "opacity"]}
        >
          <h2 className="text-textPrimary text-heading2xl">
            {getText("Permit requirements")}
          </h2>

          <p className="text-textSubdued text-bodylg">
            {getText(
              "Planning, local law and other permits your proposal likely needs before it can go ahead.",
            )}
          </p>
        </motion.div>
      </div>

      <div className="empty:hidden" id="content-header-portal" />

      {reportGenerating ? (
        <div className="overflow-hidden">
          <motion.div
            className="[transform-origin:top_left]"
            variants={animationVariants}
            initial={!skipAnimations && "hidden"}
            animate={["loader", "opacity"]}
          >
            <PermitRequirementsLoaderContainer
              noticeboardData={noticeboardData}
              latestUserMessage={latestUserMessage}
              progressIndicatorItemMap={reportLoadingIndicatorConfig}
              title={getText("🤓 Generating permit requirements")}
            />
          </motion.div>
        </div>
      ) : null}

      {reportReGenerating ? (
        <div className="overflow-hidden">
          <motion.div
            className="[transform-origin:top_left]"
            variants={animationVariants}
            initial={!skipAnimations && "hidden"}
            animate={["loader", "opacity"]}
          >
            <PermitRequirementsLoaderContainer
              noticeboardData={noticeboardData}
              latestUserMessage={latestUserMessage}
              progressIndicatorItemMap={reportLoadingIndicatorConfig}
              title={getText("🤓 Re-generating permit requirements")}
            />
          </motion.div>
        </div>
      ) : null}

      {!reportGenerating && !reportReGenerating ? (
        <>
          <div className="overflow-hidden">
            <motion.div
              className="[transform-origin:top_left]"
              variants={animationVariants}
              initial={!skipAnimations && "hidden"}
              animate={["timeline", "opacity"]}
            >
              <AnalysisTimeline conversation={mergedConversation} />
            </motion.div>
          </div>

          <div className="overflow-hidden">
            <motion.div
              className="flex flex-col gap-10 [transform-origin:top_left]"
              variants={animationVariants}
              initial={!skipAnimations && "hidden"}
              animate={["report", "opacity"]}
            >
              <PermitRequirementsReport
                conversation={mergedConversation}
                messages={messages || []}
                noticeboardData={noticeboardData}
                printable={printable}
                translate={{
                  getText,
                  interpolate,
                }}
                configPrefix="enquiry"
              />

              {printable ? null : (
                <>
                  <div className="empty:hidden" id="content-footer-portal" />

                  <Separator orientation="horizontal" />

                  <ReportWarningPanel conversation={mergedConversation} />

                  <MultiLingualDisclaimer />
                </>
              )}
            </motion.div>
          </div>
        </>
      ) : null}
    </div>
  );
}
